import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import { useCurrentUser } from '@ingenico/react-section-utils';
import { CoreTypography, CoreTypographyVariant } from '@ingenico/react-ui/core-typography';
import { useRenderDefaultColumHeader } from '@ingenico/react-ui/datagrid';
import { Box } from '@mui/material';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import type { ReactNode } from 'react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../components/logical-device-applications-card/logicalDeviceApplicationsCard.module.scss';
import type { Applications } from '../domain/models';

interface UseLogicalDeviceApplicationColumnsReturn {
  readonly defaultAvailableColumns: string[];
  readonly columns: GridColDef[];
}

export function useLogicalDeviceApplicationColumns(): UseLogicalDeviceApplicationColumnsReturn {
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.LOGICAL_DEVICE, ALL_LNGS_NAMESPACES.COMMON]);
  const _currentUser = useCurrentUser();
  const { defaultRenderColumnHeader } = useRenderDefaultColumHeader(t);

  const renderCellName = useCallback(
    ({ row: { packageName } }: GridRenderCellParams<Applications, Applications>): ReactNode => {
      return (
        <Box className={styles.nameField}>
          <CoreTypography
            customClassName={styles.nameField__text}
            showTooltip
            tooltipTitle={packageName}
            truncateText
            variant={CoreTypographyVariant.Body02Medium}
          >
            {packageName || t('softwareList.unknownValue')}
          </CoreTypography>
        </Box>
      );
    },
    [t],
  );

  const renderCellVersion = useCallback(
    ({ row: { version } }: GridRenderCellParams<Applications, Applications>): ReactNode => {
      return (
        <Box className={styles.nameField}>
          <CoreTypography
            customClassName={styles.nameField__text}
            showTooltip
            tooltipTitle={version}
            truncateText
            variant={CoreTypographyVariant.Body02Medium}
          >
            {version || t('softwareList.unknownValue')}
          </CoreTypography>
        </Box>
      );
    },
    [t],
  );

  const columns = useMemo(
    () => [
      {
        field: 'packageName',
        renderCell: renderCellName,
        renderHeader: defaultRenderColumnHeader('softwareList.field.name'),
        headerName: t('softwareList.field.name'),
        filterable: false,
        flex: 1.5,
      },
      {
        field: 'versionCode',
        renderHeader: defaultRenderColumnHeader('softwareList.field.version'),
        headerName: t('softwareList.field.version'),
        renderCell: renderCellVersion,
        filterable: false,
        flex: 1,
      },
    ],
    [renderCellName, defaultRenderColumnHeader, t, renderCellVersion],
  );

  const defaultAvailableColumns = columns.map(({ field }) => field);

  return {
    columns,
    defaultAvailableColumns,
  };
}
